import { useNavigate, useOutlet } from "react-router-dom";
import { Button, Divider, Grid, Group, Paper, Select, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import ShipmentQuickTrack from "../../components/project/Shipment.quick.track/Shipment.quick.track";
import { CSSProperties, useState } from "react";
import ShipmentMilestoneTable from "../../components/project/Shipment.Milestone.Table/Shipment.Milestone.Table";
import {
  useFinalDeliveries,
  useShipmentCSRList, useShipments,
  useShipmentsMilestonesAggregation
} from "../../api/Shipment/Shipment.hooks";
import LoaderDefault from "../../components/common/Loader.default/Loader.default";
import ShipmentTable from "../../components/project/Shipment.table/Shipment.table";
import { ArrowLeft, Recycle } from "tabler-icons-react";
import useUserStore from "../../data/User.store";
import useShipmentQueryStore from "../../data/ShipmentQuery.store";

function Shipments() {
  const { user } = useUserStore();
  const { setShipmentQuery: setStoreShipmentQuery } = useShipmentQueryStore();
  const outlet = useOutlet();
  const theme = useMantineTheme();
  const [shipmentMilestoneAggregationsQuery, setShipmentMilestoneAggregationsQuery] = useState<any>({ shippers: user?.shippers });
  const [csrQuery, setCSRQuery] = useState<any>({ shippers: user?.shippers });
  const [finalDeliveriesQuery, setFinalDeliveriesQuery] = useState<any>({ shippers: user?.shippers });
  const [selectedCsr, setSelectedCsr] = useState<any>();
  const [shipmentQuery, setShipmentQuery] = useState<any>();
  const navigate = useNavigate();

  const { isLoading: shipmentsLoading, data: shipments } = useShipments(shipmentQuery)
  const {
    isLoading: isLoadingMilestoneAggregations,
    data: milestoneAggregations
  } = useShipmentsMilestonesAggregation(shipmentMilestoneAggregationsQuery);
  const { isLoading: isLoadingCSRList, data: csrList } = useShipmentCSRList(csrQuery);
  const { isLoading: isLoadingFinalDeliveries, data: finalDeliveries } = useFinalDeliveries(finalDeliveriesQuery);


  if (outlet) {
    return outlet;
  }

  const commonPaperStyle: CSSProperties = {
    position: 'relative',
    width: "100%",
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
  }

  if (isLoadingCSRList || isLoadingFinalDeliveries || shipmentsLoading) {
    return <LoaderDefault text="Loading data..." />
  }

  function renderTable() {
    if (isLoadingMilestoneAggregations) {
      return <LoaderDefault text="Loading milestone data..." />
    } else if (!milestoneAggregations) {
      return <Text>No Shipment Milestone Data</Text>
    } else {
      return <ShipmentMilestoneTable data={milestoneAggregations} onMilestoneClick={onMilestoneClick} />
    }
  }

  function onMilestoneClick(milestone: string) {
    let query = {
      dashboardMilestone: milestone,
      shippers: user?.shippers,
      csr: selectedCsr
    };
    setStoreShipmentQuery({ query, searchType: "milestone" });
    navigate(`/shipment-tracking/shipments`)
  }

  function onShipmentQuickTrackSubmit(shipmentQuickTrackQuery: any) {
    setStoreShipmentQuery({ query: shipmentQuickTrackQuery, searchType: "quickTrack" });
    navigate(`/shipment-tracking/shipments`)
  }

  function onCSRChange(csr: string) {
    setSelectedCsr(csr);
    setShipmentMilestoneAggregationsQuery({ shippers: user?.shippers, csr });
  }

  function onFinalDeliveryChange(finalDelivery: string) {
    setStoreShipmentQuery({
      query: { trackingType: "PlaceOfDelivery", searchType: "Exact", query: finalDelivery },
      searchType: "quickTrack"
    });
    navigate(`/shipment-tracking/shipments`)
  }

  function getFinalDeliveries() {
    let ret: any = {};
    if (finalDeliveries) {
      finalDeliveries.forEach(fd => {
        ret[fd.location] = fd.location;
      })
    }
    return Object.keys(ret).map(location => ({ value: location, label: location }));
  }

  return (
    <Stack style={{ maxWidth: 1500 }}>
      <Group>
        <Title style={{ color: "#C1C2C5" }}>
          Shipment Tracking
        </Title>
      </Group>
      <Grid>
        <Grid.Col lg={6}>
          <Paper p={16} style={{ ...commonPaperStyle }}>
            <ShipmentQuickTrack onSubmitQuery={onShipmentQuickTrackSubmit} />
          </Paper>
          <Paper p={16} mt={16} style={{ ...commonPaperStyle }}>
            <Title order={3}>
              Final Delivery
            </Title>
            <Divider my={16} />
            {finalDeliveries &&
              <Select placeholder="Final Delivery"
                data={getFinalDeliveries()}
                mb={12} clearable
                onChange={onFinalDeliveryChange} />}
          </Paper>
        </Grid.Col>
        <Grid.Col lg={6}>
          <Paper p={16} style={{ ...commonPaperStyle }}>
            <Title order={3}>
              Summary
            </Title>
            <Divider my={16} />
            {csrList && <Select placeholder="CSR Filter Criteria"
              data={csrList?.map(csr => ({ value: csr.record, label: csr.record }))} mb={12}
              clearable
              onChange={onCSRChange} value={selectedCsr} />}
            {renderTable()}
          </Paper>
        </Grid.Col>
      </Grid>
    </Stack>
  )
}

export default Shipments;